// Types partagés client — vue « Capacité hebdomadaire » + section taux/occupation.
// Le gabarit « atelier » est devenu `prestation`, classé sur deux axes catégorie × pôle.

// ── Vue principale : Capacité hebdomadaire (reproduit l'onglet Excel) ────────
export interface LigneCapacite {
  prestationId: number;
  prestationNom: string;
  categorieId: number;
  categorieNom: string;
  poleId: number;
  poleNom: string;
  nbSemaines: number;                  // semaines où l'activité a eu lieu
  participantsActifs: number | null;   // participants/sem (quand ça tourne)
  picHebdo: number;                    // pic hebdo (max simultanés)
  participantsMoyenne: number | null;  // participants/sem (moyenne sur nbSemainesBase)
  presencesParSemaine: number | null;  // présences totales/sem (occurrences)
  joursParParticipant: number | null;  // jours de présence / participant / sem
  totalPresences: number;              // total présences (période)
  totalHeures: number;                 // total heures (période)
  heuresParParticipant: number | null; // heures / semaine / participant
  niveauUsage: 'Usage fort' | 'Usage modéré' | 'Usage faible';
}

export interface CapaciteReponse {
  filtre: Filtre;
  nbSemainesBase: number;
  participantsDistinct: number;
  lignes: LigneCapacite[];
}

// ── Section secondaire : taux de présence & heures par prestation ───────────
export interface LignePrestation {
  prestationId: number;
  prestationNom: string;
  categorieNom: string | null;
  poleNom: string | null;
  lieuNom: string | null;
  jourSemaine: number | null;
  intervenantNom: string | null;
  capacite: number | null;
  seances: number;
  inscrits: number;
  presences: number;
  presencesTheoriques: number; // dénominateur — « dispensed » exclu
  dispenses: number;
  heuresRealisees: number;
  tauxInscription: number | null;
  tauxPresence: number | null;
  tauxOccupation: number | null;
}

export interface OccupationSalle {
  lieuId: number;
  lieuNom: string | null;
  heuresRealisees: number;
  heuresDisponibles: number | null;
  tauxOccupationSalles: number | null;
  configuree: boolean;
}

export interface Filtre {
  from: string;
  to: string;
  mandantId?: number;
  lieuId?: number;
  categorieIds?: number[];
}

export interface PrestationsReponse {
  filtre: Filtre;
  lignes: LignePrestation[];
  salles: OccupationSalle[];
}

// ── Référentiels de filtres ─────────────────────────────────────────────────
export interface Ref { id: number; nom: string; }
export interface RefMesure { id: number; nom: string; mandant: string | null; }
export interface RefPublic { value: string; label: string; }
export interface RefGroupe { code: string; nom: string; umbrella: string | null; }
export interface RefMandant { id: number; nom: string; groupe: string; }
export interface Filtres {
  lieux: Ref[];
  categories: Ref[];
  poles: Ref[];             // domaines v2.1 (ex-pôles)
  groupes: RefGroupe[];     // familles de mandants (funder_group)
  mandants: RefMandant[];   // drilldown par famille
  mesures: RefMesure[];
  publics: RefPublic[];
}

export const JOURS = ['—', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// ── Vue analytique consolidée (les 3 espaces de données) ─────────────────────
export interface AnalyticsFiltre {
  from: string;
  to: string;
  publics?: string[];
  categorieIds?: number[];
  poleIds?: number[];
  mesureIds?: number[];
  lieuIds?: number[];
  mandantIds?: number[];   // drilldown mandants
  socioPro?: boolean;      // préréglage socio-pro (décision opérateur 2026-08-28)
}

export interface LigneImpactCtt {
  lieuNom: string; heures: number; participants: number;
  tauxAbsenteisme: number | null; heuresCtt: number; partSite: number;
  restantes: number; partDesCtt: number; exposition: 'Élevée' | 'Moyenne' | 'Faible';
}
export interface LigneVocation { lieuNom: string; poleNom: string; heures: number }
export interface LigneDetailParticipant {
  participantId: number; nom: string; publics: string[]; mesures: string[];
  familles: string[]; nbFamilles: number; seancesPresentes: number;
  heures: number; absences: number; dispenses: number; tauxAbsenteisme: number | null;
}

export interface LigneMesureConso {
  mesureId: number; nom: string; publicType: string; mandantNom: string | null;
  participants: number; presences: number; tauxAbsenteisme: number | null;
  heures: number; pctHeures: number;
}
export interface LigneFamilleMois { mois: string; publicType: string; heures: number }

export interface AnalyticsReponse {
  filtre: AnalyticsFiltre;
  nbSemainesBase: number;
  kpis: {
    participantsDistinct: number;
    presences: number;
    absences: number;        // excusées + injustifiées (dispensé·es exclus)
    dispenses: number;
    tauxAbsenteisme: number; // dénominateur hors dispensé·es
    heuresRealisees: number;
    heuresPlanifiees: number;
    heuresPerdues: number;
  };
  effort: { mode: 'categorie' | 'pole'; items: { label: string; heures: number }[] };
  capacite: LigneCapacite[];
  espaces: {
    tauxOccupationMoyen: number | null;
    sitesActifs: number;
    sitesTotal: number;
    occupation: OccupationSalle[];
    placement: { intervenantNom: string; seances: number; heures: number }[];
    planifieVsRealise: { lieuId: number; lieuNom: string | null; planifie: number; realise: number; perdu: number; pctPerdu: number }[];
    mixite: { lieuId: number; lieuNom: string | null; parPublic: Record<string, number>; total: number }[];
    evolution: { mode: 'semaine' | 'mois'; points: { label: string; heures: number }[] };
  };
  mesures: {
    ai: { mesureId: number; nom: string; participants: number; presences: number; tauxAbsenteisme: number; heures: number; pctHeures: number }[];
    ri: { label: string; cle: string; heures: number }[];
  };
  impactCtt: LigneImpactCtt[];
  vocation: LigneVocation[];
  mesuresToutes: LigneMesureConso[];
  famillesMois: LigneFamilleMois[];
}

// Valeurs de la dimension « public » et leur clé de couleur catégorielle.
export const PUBLIC_COULEUR: Record<string, string> = {
  AI: 'var(--s-ai)',
  'RI-CCC': 'var(--s-ccc)',
  'RI-CTT': 'var(--s-ctt)',
  SISP: 'var(--s-sisp, #7c5cff)',
  Autres: 'var(--s-autre)',
};
